import React, { useRef, useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import apiClient from "@/services";
import getConstants from "@/scripts/getConstants";
import { getUser } from "@/hooks/useLocalStorage";
import getInterestedUsers from "@/scripts/getInterestedUsers";
import { handleImageUpload } from "@/scripts/uploadEmailImages";
import { ImageIcon } from "lucide-react";

const emailSchema = z.object({
  subject: z
    .string()
    .min(1, "Subject is required")
    .max(100, "Subject too long"),
  recipients: z
    .string()
    .refine(
      (val) =>
        val
          .split(",")
          .every((email) => z.string().email().safeParse(email.trim()).success),
      { message: "Invalid email list" }
    ),
  template: z.string().min(1, "Email template is required"),
});

const productSchema = z.object({
  grade: z.string().min(2, { message: "Grade of material is required" }),
  form: z.string().min(2, { message: "Form of material is required" }),
  type: z.string().min(2, { message: "Type of client is required" }),
});

function copyToClipboard(emails) {
  // Convert the array to a comma-separated string
  const textToCopy = emails.join(",");

  // Copy the string to the clipboard
  navigator.clipboard
    .writeText(textToCopy)
    .then(() => {
      console.log("Copied to clipboard");
    })
    .catch((error) => {
      console.error("Failed to copy to clipboard:", error);
    });
}

function EmailMarketing() {
  const user = getUser();
  const editorRef = useRef(null);
  const [mode, setMode] = useState(1);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  // Constants
  const [forms, setForms] = useState([]);
  const [grades, setGrades] = useState([]);
  // Relevant Clients
  const [relevantClients, setRelevantClients] = useState();

  const form = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      subject: "",
      recipients: "",
      template: "",
    },
  });

  const form2 = useForm({
    resolver: zodResolver(productSchema),
    defaultValues: {
      grade: "",
      form: "",
      type: "",
    },
  });

  // Update the template form value when the contenteditable div changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleInput = () => {
      form.setValue("template", editor.innerHTML, {
        shouldValidate: true,
        shouldDirty: true,
      });
    };

    editor.addEventListener("input", handleInput);
    return () => {
      editor.removeEventListener("input", handleInput);
    };
  }, [form, editorRef.current]);

  const onSubmit = async (values) => {
    try {
      setLoading(true);

      // 1. Start with the base template
      let finalHtml = values.template;

      // 2. Append all images at the end (if they exist)
      if (images?.length > 0) {
        finalHtml += images
          .map(
            (imgUrl) => `
              <br />
              <img src="${imgUrl}" 
                alt="Marketing content" 
                width="600" 
                style="
                  display: block; 
                  width: 100%; 
                  max-width: 600px; 
                  height: auto; 
                  border: 0; 
                  outline: none; 
                  text-decoration: none; 
                  margin-top: 20px; /* Add spacing between images */
                "
              >
            `
          )
          .join(""); // Combine all images into a single string
      }

      // 3. Wrap in a centered container (better email client support)
      finalHtml = `<div style="max-width:600px; margin:0 auto;">${finalHtml}</div>`;

      const response = await apiClient.post("/emailMarketing", {
        subject: values.subject,
        html: `<div style="max-width:600px; margin:0 auto;">${finalHtml}</div>`, // Wrapped for better email client rendering
        timestamp: new Date().toLocaleString(),
        recipients: values.recipients.split(",").map((email) => email.trim()),
        // Include images array in case backend needs it
        images: values.images || [],
      });

      console.log(response.data);
      alert("Marketing Emails sent successfully!");
      form.reset();
    } catch (err) {
      console.log("Error sending bulk emails", err);
      alert("Failed to send emails. Please try again.");
    } finally {
      setLoading(false);
      setImages([]);
      if (editorRef.current) {
        editorRef.current.innerHTML = "";
      }
    }
  };

  // Function to apply formatting
  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    // Update the form value after formatting
    if (editorRef.current) {
      form.setValue("template", editorRef.current.innerHTML, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  // Function to handle image upload
  const handleImage = async (event) => {
    if (!event?.target) return;

    setImgLoading(true);
    try {
      const files = event.target.files;
      if (!files?.length) return;

      const urls = await handleImageUpload(
        Array.from(files),
        "email",
        user.uid
      );

      setImages([...images, ...urls]);
      return urls;
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setImgLoading(false);
    }
  };

  // Constant Values
  const fetchConstants = async () => {
    try {
      setLoading(true);
      const { forms, grades } = await getConstants();
      // Set values
      setForms(forms);
      setGrades(grades);
    } catch (err) {
      console.log("Unable to fetch constants", err);
    } finally {
      setLoading(false);
    }
  };

  // Find Relevant Clients
  const findClients = async (values) => {
    try {
      setLoading(true);
      const list = await getInterestedUsers(values);
      setRelevantClients(list);
    } catch (err) {
      console.log("Error fetching relevant clients", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === 2) {
      fetchConstants();
    }
  }, [mode]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper">
      <div className="flex gap-5">
        <div className="cursor-pointer" onClick={() => setMode(1)}>
          <h1 className="text-sm font-bold text-green ml-0.5 w-[110px]">
            Compose Email
          </h1>
          {mode === 1 && <div className="highlight !w-[110px]" />}
        </div>

        <div className="cursor-pointer" onClick={() => setMode(2)}>
          <h1 className="text-sm font-bold text-green ml-0.5 w-[110px]">
            Relevant Clients
          </h1>
          {mode === 2 && <div className="highlight !w-[112px]" />}
        </div>
      </div>

      {mode === 1 && (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 p-3"
          >
            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Subject</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter email subject" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="recipients"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Recipients (comma-separated emails)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="john@example.com, jane@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="template"
              render={({}) => (
                <FormItem>
                  <FormLabel>Email Template</FormLabel>
                  <div className="border rounded-lg p-2 bg-white">
                    {/* Toolbar for formatting options */}
                    <div className="flex gap-2 mb-2">
                      <Button
                        type="button"
                        onClick={() => formatText("bold")}
                        className="p-1 w-[60px]"
                      >
                        <strong>Bold</strong>
                      </Button>
                      <Button
                        type="button"
                        onClick={() => formatText("italic")}
                        className="p-1 w-[60px]"
                      >
                        <em>Italic</em>
                      </Button>
                      {/* <Button
                        type="button"
                        onClick={() => formatText("formatBlock", "<h1>")}
                        className="p-1 w-[40px]"
                      >
                        H1
                      </Button>
                      <Button
                        type="button"
                        onClick={() => formatText("formatBlock", "<h2>")}
                        className="p-1 w-[40px]"
                      >
                        H2
                      </Button>
                      <Button
                        type="button"
                        onClick={() => formatText("insertUnorderedList")}
                        className="p-1 w-[60px]"
                      >
                        • List
                      </Button> */}

                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImage(e)}
                        className="hidden"
                        id="image-upload"
                      />
                      <label
                        htmlFor="image-upload"
                        className="bg-black text-white text-center text-sm rounded-md p-1 cursor-pointer px-3 flex flex-row items-center gap-1"
                      >
                        <ImageIcon height={16} width={16} />
                        {imgLoading ? "Uploading..." : "Image"}
                      </label>
                    </div>

                    {/* Contenteditable div for the editor */}
                    <div
                      ref={editorRef}
                      contentEditable
                      className="min-h-[200px] p-2 border rounded-lg"
                      placeholder="Write your email template here..."
                    />
                    <FormMessage />

                    {images.length > 0 && (
                      <div className="flex flex-row gap-4 p-2">
                        {images.map((img, i) => (
                          <img
                            src={img}
                            key={i}
                            className="h-[100px] w-[100px]"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </FormItem>
              )}
            />

            {!imgLoading && (
              <Button type="submit" className="w-full bg-green">
                Send
              </Button>
            )}
          </form>
        </Form>
      )}

      {mode === 2 && (
        <Form {...form2}>
          <form
            className="space-y-4 p-3"
            onSubmit={form2.handleSubmit(findClients)}
          >
            <p className="mt-2 text-sm">
              Find Interested Buyers or Sellers Filter users based on product
              grade, form, and client type to find the perfect match for your
              needs.
            </p>
            <div className="flex justify-between mobileFormWrapper">
              {/* Grade */}
              <div className="w-[49%] mobileInputWrapper">
                <FormField
                  control={form2.control}
                  name="grade"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Grade of Material</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl className="h-10">
                          <SelectTrigger>
                            <SelectValue placeholder="Select grade" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {grades.map((grade) => (
                            <SelectItem key={grade} value={grade}>
                              {grade}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Form */}
              <div className="w-[49%] mobileInputWrapper">
                <FormField
                  control={form2.control}
                  name="form"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Form of Material</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl className="h-10">
                          <SelectTrigger>
                            <SelectValue placeholder="Select form" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {forms.map((form) => (
                            <SelectItem key={form} value={form}>
                              {form}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-between mobileFormWrapper">
              {/* Grade */}
              <div className="w-[49%] mobileInputWrapper">
                <FormField
                  control={form2.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type of Client</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl className="h-10">
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {["Buyer", "Seller", "Both"].map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Button type="submit" className="w-full bg-green">
              Search
            </Button>

            {relevantClients?.length === 0 && <p>Result: No users found!</p>}

            {relevantClients?.length > 0 && (
              <div>
                <p className="mb-2 text-sm">
                  Result: {relevantClients.length} users found!
                </p>
                <Textarea defaultValue={relevantClients} />
                <Button
                  type="button"
                  className="w-full bg-green mt-4"
                  onClick={() => copyToClipboard(relevantClients)}
                >
                  Copy
                </Button>
              </div>
            )}
          </form>
        </Form>
      )}
    </div>
  );
}

export default EmailMarketing;
